import React, { useEffect, useState, useCallback } from "react";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { heroSlides } from "../mock";

const Hero = () => {
  const [active, setActive] = useState(0);
  const navigate = useNavigate();

  const next = useCallback(() => setActive((a) => (a + 1) % heroSlides.length), []);
  const prev = () => setActive((a) => (a - 1 + heroSlides.length) % heroSlides.length);

  useEffect(() => {
    const t = setInterval(next, 6500);
    return () => clearInterval(t);
  }, [next, active]);

  return (
    <section className="relative min-h-screen flex flex-col lg:flex-row bg-[#050f24] overflow-hidden" data-testid="hero">
      {/* Left content panel */}
      <div className="relative z-20 lg:w-[47%] flex flex-col justify-center px-6 sm:px-10 lg:px-16 pt-32 pb-14 lg:py-0 bg-gradient-to-br from-[#050f24] via-[#071d3f] to-[#0a2b55]">
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "46px 46px",
            maskImage: "radial-gradient(circle at 30% 40%, black, transparent 75%)",
          }}
        />
        <div className="absolute -left-24 top-1/4 w-[380px] h-[380px] rounded-full bg-white/10 blur-3xl" />

        {heroSlides.map((slide, i) => (
          <div
            key={slide.id}
            className={`relative max-w-xl ${i === active ? "block" : "hidden"}`}
          >
            <div className="inline-flex items-center gap-2 mb-6">
              <span className="w-8 h-[2px] bg-white" />
              <span className="text-white text-sm font-semibold tracking-[0.16em] uppercase">{slide.tag}</span>
            </div>
            <h1
              className="text-white font-bold leading-[1.06] text-[40px] sm:text-[52px] lg:text-[60px] animate-slideup"
              style={{ fontFamily: "Archivo, sans-serif" }}
            >
              {slide.title.map((line, j) => (
                <span key={j} className="block">
                  {line}
                </span>
              ))}
            </h1>
            <p className="text-white/70 text-lg leading-relaxed mt-6 max-w-md animate-slideup" style={{ animationDelay: "120ms" }}>
              {slide.subtitle}
            </p>
            <button
              onClick={() => navigate(slide.link)}
              className="mt-8 inline-flex items-center gap-2 bg-white hover:bg-[#d7e3f4] text-[#04122e] text-sm font-bold px-7 py-3.5 rounded-full transition-all hover:gap-3.5 shadow-[0_0_24px_rgba(255,255,255,0.35)] animate-slideup"
              style={{ animationDelay: "240ms" }}
            >
              {slide.cta} <ArrowRight size={17} />
            </button>
          </div>
        ))}

        {/* Controls */}
        <div className="relative z-20 flex items-center gap-5 mt-12">
          <div className="flex items-center gap-3">
            <button
              onClick={prev}
              aria-label="Previous slide"
              className="w-11 h-11 rounded-full border border-white/30 text-white flex items-center justify-center hover:bg-white hover:border-white hover:text-[#04122e] transition-colors"
            >
              <ArrowLeft size={17} />
            </button>
            <button
              onClick={next}
              aria-label="Next slide"
              className="w-11 h-11 rounded-full border border-white/30 text-white flex items-center justify-center hover:bg-white hover:border-white hover:text-[#04122e] transition-colors"
            >
              <ArrowRight size={17} />
            </button>
          </div>
          <div className="flex gap-2">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${i === active ? "w-9 bg-white" : "w-4 bg-white/30 hover:bg-white/60"}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right image panel */}
      <div className="relative lg:w-[53%] min-h-[300px] flex-1">
        {heroSlides.map((slide, i) => (
          <div key={slide.id} className={`absolute inset-0 transition-opacity duration-1000 ${i === active ? "opacity-100" : "opacity-0"}`}>
            <img
              src={slide.image}
              alt={slide.title.join(" ")}
              className={`w-full h-full object-cover ${i === active ? "animate-kenburns" : ""}`}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#050f24] via-[#050f24]/20 to-transparent lg:from-[#0a2b55]/80 lg:via-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050f24]/60 via-transparent to-transparent" />
          </div>
        ))}
        {/* neon edge */}
        <div className="hidden lg:block absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-transparent via-white to-transparent" />
      </div>
    </section>
  );
};

export default Hero;
